import { AIR, VOXELS_PER_METER } from '../constants';
import type { VoxelWorld } from './world';

export interface VoxelHit {
  /** Voxel that was hit. */
  x: number;
  y: number;
  z: number;
  /** Face normal of the entry face (points back toward the ray origin). */
  nx: number;
  ny: number;
  nz: number;
  /** Distance along the ray in meters. */
  dist: number;
  value: number;
}

export const newHit = (): VoxelHit => ({ x: 0, y: 0, z: 0, nx: 0, ny: 0, nz: 0, dist: 0, value: 0 });

/**
 * Amanatides-Woo voxel traversal. Origin in meters, direction must be normalized.
 * Visits voxels in order; `visit` returns true to stop. Returns true if stopped by `visit`.
 */
export function traverseVoxels(
  world: VoxelWorld,
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  maxDist: number,
  visit: (x: number, y: number, z: number, dist: number, nx: number, ny: number, nz: number) => boolean,
): boolean {
  const s = VOXELS_PER_METER;
  const px = ox * s;
  const py = oy * s;
  const pz = oz * s;
  let x = Math.floor(px);
  let y = Math.floor(py);
  let z = Math.floor(pz);
  const stepX = dx > 0 ? 1 : dx < 0 ? -1 : 0;
  const stepY = dy > 0 ? 1 : dy < 0 ? -1 : 0;
  const stepZ = dz > 0 ? 1 : dz < 0 ? -1 : 0;
  const tDeltaX = stepX !== 0 ? Math.abs(1 / dx) : Infinity;
  const tDeltaY = stepY !== 0 ? Math.abs(1 / dy) : Infinity;
  const tDeltaZ = stepZ !== 0 ? Math.abs(1 / dz) : Infinity;
  let tMaxX = stepX > 0 ? (x + 1 - px) * tDeltaX : stepX < 0 ? (px - x) * tDeltaX : Infinity;
  let tMaxY = stepY > 0 ? (y + 1 - py) * tDeltaY : stepY < 0 ? (py - y) * tDeltaY : Infinity;
  let tMaxZ = stepZ > 0 ? (z + 1 - pz) * tDeltaZ : stepZ < 0 ? (pz - z) * tDeltaZ : Infinity;
  const maxT = maxDist * s;
  let t = 0;
  let nx = 0;
  let ny = 0;
  let nz = 0;
  const sx = world.sx;
  const sy = world.sy;
  const sz = world.sz;
  for (let guard = 0; guard < 100000; guard++) {
    // Leaving the map (except upward into the sky, which can still come back down).
    if (x < -1 || z < -1 || x > sx || z > sz || y < -1) return false;
    if (y >= sy && stepY >= 0) return false;
    if (visit(x, y, z, t / s, nx, ny, nz)) return true;
    if (tMaxX < tMaxY) {
      if (tMaxX < tMaxZ) {
        t = tMaxX;
        x += stepX;
        tMaxX += tDeltaX;
        nx = -stepX;
        ny = 0;
        nz = 0;
      } else {
        t = tMaxZ;
        z += stepZ;
        tMaxZ += tDeltaZ;
        nx = 0;
        ny = 0;
        nz = -stepZ;
      }
    } else if (tMaxY < tMaxZ) {
      t = tMaxY;
      y += stepY;
      tMaxY += tDeltaY;
      nx = 0;
      ny = -stepY;
      nz = 0;
    } else {
      t = tMaxZ;
      z += stepZ;
      tMaxZ += tDeltaZ;
      nx = 0;
      ny = 0;
      nz = -stepZ;
    }
    if (t > maxT) return false;
  }
  return false;
}

/** First solid voxel along a ray. */
export function raycastVoxels(
  world: VoxelWorld,
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  maxDist: number,
  out: VoxelHit,
): boolean {
  return traverseVoxels(world, ox, oy, oz, dx, dy, dz, maxDist, (x, y, z, dist, nx, ny, nz) => {
    const v = y < 0 ? 1 : world.get(x, y, z);
    if (v === AIR) return false;
    out.x = x;
    out.y = y;
    out.z = z;
    out.nx = nx;
    out.ny = ny;
    out.nz = nz;
    out.dist = dist;
    out.value = v;
    return true;
  });
}

/** True when the straight segment between two points crosses no solid voxel. */
export function lineOfSight(
  world: VoxelWorld,
  ax: number,
  ay: number,
  az: number,
  bx: number,
  by: number,
  bz: number,
): boolean {
  const dx = bx - ax;
  const dy = by - ay;
  const dz = bz - az;
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (len < 1e-6) return true;
  const hit = traverseVoxels(world, ax, ay, az, dx / len, dy / len, dz / len, len, (x, y, z) => world.get(x, y, z) !== AIR);
  return !hit;
}

/** Ray vs axis-aligned box (slab test). Returns entry distance or -1. */
export function rayAabb(
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  minX: number,
  minY: number,
  minZ: number,
  maxX: number,
  maxY: number,
  maxZ: number,
  maxDist: number,
): number {
  let tmin = 0;
  let tmax = maxDist;
  if (Math.abs(dx) < 1e-12) {
    if (ox < minX || ox > maxX) return -1;
  } else {
    const inv = 1 / dx;
    let t1 = (minX - ox) * inv;
    let t2 = (maxX - ox) * inv;
    if (t1 > t2) {
      const t = t1;
      t1 = t2;
      t2 = t;
    }
    tmin = Math.max(tmin, t1);
    tmax = Math.min(tmax, t2);
    if (tmin > tmax) return -1;
  }
  if (Math.abs(dy) < 1e-12) {
    if (oy < minY || oy > maxY) return -1;
  } else {
    const inv = 1 / dy;
    let t1 = (minY - oy) * inv;
    let t2 = (maxY - oy) * inv;
    if (t1 > t2) {
      const t = t1;
      t1 = t2;
      t2 = t;
    }
    tmin = Math.max(tmin, t1);
    tmax = Math.min(tmax, t2);
    if (tmin > tmax) return -1;
  }
  if (Math.abs(dz) < 1e-12) {
    if (oz < minZ || oz > maxZ) return -1;
  } else {
    const inv = 1 / dz;
    let t1 = (minZ - oz) * inv;
    let t2 = (maxZ - oz) * inv;
    if (t1 > t2) {
      const t = t1;
      t1 = t2;
      t2 = t;
    }
    tmin = Math.max(tmin, t1);
    tmax = Math.min(tmax, t2);
    if (tmin > tmax) return -1;
  }
  return tmin;
}
